import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EnumMultiSelectControl, Form, TextInputControl } from '@/core/components';

import { allRoleTypes } from '@feature/security';
import { useAppNotification } from '@feature/utils';

import { useUserCreate } from './model/UserQuery';
import { UserCreateSchema, defaultUserCreateValues, toUserCreateRequest, userCreateSchema } from './model/UserSchema';

interface LocalAccountCreateProps {
  onClose: () => void;
}

export function LocalAccountCreate({ onClose }: LocalAccountCreateProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { mutate, isPending } = useUserCreate();

  const form = useForm<UserCreateSchema>({
    defaultValues: defaultUserCreateValues,
    resolver: zodResolver(userCreateSchema),
  });

  const roleOptions = allRoleTypes.map(role => ({ value: role, label: t(`roleType.${role}`) }));

  // A second submit in the same frame would answer USER_EMAIL_EXISTS after the account was created.
  const inFlight = useRef(false);

  const onSubmit = (values: UserCreateSchema) => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    mutate(toUserCreateRequest(values), {
      onError: error => {
        inFlight.current = false;
        notification.error(error);
      },
      onSuccess: () => {
        notifications.show({
          color: 'green',
          title: t('notification.success.title'),
          message: t('localAccounts.created'),
        });
        onClose();
      },
    });
  };

  return (
    <Modal
      opened
      centered
      onClose={onClose}
      title={t('localAccounts.form.title')}
    >
      <Text
        size="sm"
        c="dimmed"
      >
        {t('localAccounts.form.description')}
      </Text>
      <FormProvider {...form}>
        <Form
          onSubmit={form.handleSubmit(onSubmit)}
          onCancel={onClose}
          isSubmitting={isPending}
        >
          <TextInputControl
            name="email"
            label={t('localAccounts.form.email')}
            isSubmitting={isPending}
            withAsterisk
            control={form.control}
          />
          <TextInputControl
            name="firstName"
            label={t('localAccounts.form.firstName')}
            isSubmitting={isPending}
            withAsterisk
            control={form.control}
          />
          <TextInputControl
            name="lastName"
            label={t('localAccounts.form.lastName')}
            isSubmitting={isPending}
            withAsterisk
            control={form.control}
          />
          <EnumMultiSelectControl
            name="roles"
            label={t('localAccounts.form.roles')}
            options={roleOptions}
            isSubmitting={isPending}
            withAsterisk
            control={form.control}
          />
        </Form>
      </FormProvider>
    </Modal>
  );
}
